import { Injectable, Injector, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { DevToolsService } from './dev-tools.service';
import { DevToolsEventLevel, DevToolsEventSource } from './dev-tools.types';
import { TaskWebSocketService } from '../services/task/task-websocket.service';
import { NotificationWebSocketService } from '../services/notification/notification-websocket.service';
import { PaymentWebSocketService } from '../services/payment/payment-websocket.service';

interface SubjectBinding {
  source: DevToolsEventSource;
  type: string;
  level: DevToolsEventLevel;
  subject$: Subject<unknown>;
}

/**
 * Branche les Subjects publics des services WS sur le DevToolsService.
 * Idempotent : `install()` ne s'abonne qu'une seule fois.
 */
@Injectable({ providedIn: 'root' })
export class DevToolsWsInterceptorService {
  private readonly dev = inject(DevToolsService);
  private readonly injector = inject(Injector);
  private installed = false;

  install(): void {
    if (!this.dev.enabled || this.installed) return;
    this.installed = true;

    try {
      const taskWs = this.injector.get(TaskWebSocketService, null);
      if (taskWs) {
        const bindings: SubjectBinding[] = [
          { source: 'task-ws', type: 'TaskListResponse', level: 'info', subject$: taskWs.onTaskListResponse$ as unknown as Subject<unknown> },
          { source: 'task-ws', type: 'TaskCreated', level: 'success', subject$: taskWs.onTaskCreated$ as unknown as Subject<unknown> },
          { source: 'task-ws', type: 'TaskAssigned', level: 'info', subject$: taskWs.onTaskAssigned$ as unknown as Subject<unknown> },
          { source: 'task-ws', type: 'TaskCompleted', level: 'success', subject$: taskWs.onTaskCompleted$ as unknown as Subject<unknown> },
          { source: 'task-ws', type: 'TaskFailed', level: 'error', subject$: taskWs.onTaskFailed$ as unknown as Subject<unknown> },
          { source: 'task-ws', type: 'TaskUpdated', level: 'info', subject$: taskWs.onTaskUpdated$ as unknown as Subject<unknown> },
        ];
        this.bindAll(bindings);
      }
    } catch {
      /* ignore */
    }

    try {
      const notifWs = this.injector.get(NotificationWebSocketService, null);
      if (notifWs) {
        this.bindAll([
          { source: 'notif-ws', type: 'NotificationNew', level: 'success', subject$: notifWs.onNotificationNew$ as unknown as Subject<unknown> },
        ]);
      }
    } catch {
      /* ignore */
    }

    try {
      const payWs = this.injector.get(PaymentWebSocketService, null);
      if (payWs) {
        this.bindAll([
          { source: 'payment-ws', type: 'InitiateResponse', level: 'info', subject$: payWs.onInitiateResponse$ as unknown as Subject<unknown> },
          { source: 'payment-ws', type: 'ButtonReady', level: 'info', subject$: payWs.onButtonReady$ as unknown as Subject<unknown> },
          { source: 'payment-ws', type: 'StatusChanged', level: 'info', subject$: payWs.onStatusChanged$ as unknown as Subject<unknown> },
          { source: 'payment-ws', type: 'StatusResponse', level: 'info', subject$: payWs.onStatusResponse$ as unknown as Subject<unknown> },
          { source: 'payment-ws', type: 'Completed', level: 'success', subject$: payWs.onCompleted$ as unknown as Subject<unknown> },
          { source: 'payment-ws', type: 'Error', level: 'error', subject$: payWs.onError$ as unknown as Subject<unknown> },
          { source: 'payment-ws', type: 'PushSent', level: 'info', subject$: payWs.onPushSent$ as unknown as Subject<unknown> },
        ]);
      }
    } catch {
      /* ignore */
    }
  }

  private bindAll(bindings: SubjectBinding[]): void {
    for (const b of bindings) {
      if (!b.subject$ || typeof b.subject$.subscribe !== 'function') continue;
      b.subject$.subscribe({
        next: (payload) => {
          const demarcheRef = this.extractDemarcheRef(payload);
          const level = this.deriveLevel(b, payload);
          this.dev.pushEvent({
            source: b.source,
            type: b.type,
            level,
            demarcheRef,
            payload,
          });
        },
        error: (err) => {
          this.dev.pushEvent({
            source: b.source,
            type: `${b.type}#error`,
            level: 'error',
            payload: err,
          });
        },
      });
    }
  }

  private extractDemarcheRef(payload: unknown): string | undefined {
    if (!payload || typeof payload !== 'object') return undefined;
    const p = payload as Record<string, unknown>;
    const candidates = ['demarcheRef', 'recordId', 'recordUid', 'record_id', 'record'];
    for (const key of candidates) {
      const v = p[key];
      if (typeof v === 'string' && v.length > 0) return v;
    }
    return undefined;
  }

  private deriveLevel(b: SubjectBinding, payload: unknown): DevToolsEventLevel {
    if (b.level === 'error') return 'error';
    if (payload && typeof payload === 'object') {
      const p = payload as Record<string, unknown>;
      const status = typeof p['status'] === 'string' ? (p['status'] as string).toLowerCase() : '';
      if (status.includes('fail') || status.includes('error')) return 'error';
      if (status.includes('success') || status.includes('completed') || status.includes('paid')) return 'success';
      if (status.includes('pending') || status.includes('wait')) return 'warn';
    }
    return b.level;
  }
}
